import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { X, MapPin, Bed, Bath, Square, Calendar, Car, Wifi, Zap, Home, Star, Navigation, Heart, MessageCircle } from 'lucide-react';
import { Property } from '../../types';
import PropertyMapWidget from './PropertyMapWidget';
import FionaPropertyAnalysis from './FionaPropertyAnalysis';
import { formatAmenityName } from '../../utils/amenityFormatter';

interface PropertyInfoPanelProps {
  property: Property;
  isOpen: boolean;
  onClose: () => void;
  matchScore: number;
}

const PropertyInfoPanel: React.FC<PropertyInfoPanelProps> = ({
  property,
  isOpen,
  onClose,
  matchScore
}) => {
  const [activeSection, setActiveSection] = useState<string>('overview');

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const amenityIcons: Record<string, React.ReactNode> = {
    'WiFi': <Wifi className="w-4 h-4" />,
    'Parking': <Car className="w-4 h-4" />,
    'Air Conditioning': <Zap className="w-4 h-4" />,
    'Laundry': <Home className="w-4 h-4" />,
    'Pool': <Navigation className="w-4 h-4" />,
    'Gym': <Star className="w-4 h-4" />
  };

  const sections = [{
    id: 'overview',
    label: 'Overview',
    icon: Home
  }, {
    id: 'location',
    label: 'Location',
    icon: MapPin
  }, {
    id: 'fiona',
    label: "Fiona's Take",
    icon: MessageCircle
  }];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - Higher priority */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-[100]"
          />

          {/* Panel - Maximized for priority */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300
            }}
            className="fixed inset-x-0 bottom-0 z-[101] h-[95vh] max-h-[95vh] overflow-hidden rounded-t-3xl"
          >
            <div className="h-full w-full bg-background backdrop-blur-xl border-t-2 border-l-2 border-r-2 border-border rounded-t-3xl shadow-2xl flex flex-col overflow-hidden">
              {/* Handle Bar */}
              <div className="flex justify-center pt-3 pb-2 flex-shrink-0 border-b border-border/10">
                <div className="w-16 h-1.5 bg-muted-foreground/40 rounded-full" />
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-hidden">
                <ScrollArea className="h-full">
                  {/* Header - Enhanced */}
                  <div className="px-6 py-6 border-b border-border/30 bg-muted/30">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <Badge className="bg-primary text-primary-foreground border-0 text-base px-4 py-1.5 font-bold shadow-sm">
                            {matchScore}% Match
                          </Badge>
                          <Badge variant="secondary" className="text-base px-4 py-1.5 font-medium">
                            {property.property_type}
                          </Badge>
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold line-clamp-2 mb-4 text-foreground">
                          {property.title}
                        </h2>
                        <div className="flex items-center text-muted-foreground text-base mb-4">
                          <MapPin className="w-5 h-5 mr-2 flex-shrink-0" />
                          <span className="line-clamp-1">
                            {property.address && `${property.address}, `}{property.city}, {property.state} {property.zip_code}
                          </span>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={onClose} 
                        className="flex-shrink-0 h-10 w-10 rounded-full hover:bg-muted"
                      >
                        <X className="w-6 h-6" />
                      </Button>
                    </div>

                    {/* Price and Key Details - Enhanced */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 bg-background/80 rounded-xl border border-border/50">
                      <div>
                        <div className="text-3xl sm:text-4xl font-bold text-primary">
                          {formatPrice(property.rent)}
                        </div>
                        <span className="text-base text-muted-foreground">per month</span>
                      </div>
                      <div className="flex items-center gap-6 text-base">
                        <div className="flex items-center gap-2 text-foreground">
                          <Bed className="w-5 h-5 text-primary" />
                          <span className="font-semibold">{property.bedrooms}</span>
                        </div>
                        <div className="flex items-center gap-2 text-foreground">
                          <Bath className="w-5 h-5 text-primary" />
                          <span className="font-semibold">{property.bathrooms}</span>
                        </div>
                        {property.square_feet && property.square_feet > 0 && (
                          <div className="flex items-center gap-2 text-foreground">
                            <Square className="w-5 h-5 text-primary" />
                            <span className="font-semibold">{property.square_feet.toLocaleString()} sqft</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section Navigation - Enhanced with horizontal scroll */}
                  <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm py-4 border-b border-border/30">
                    <div className="px-6">
                      <div className="flex gap-2 bg-muted/70 rounded-xl p-2 overflow-x-auto scrollbar-hide">
                        {sections.map(section => {
                          const Icon = section.icon;
                          return (
                            <Button
                              key={section.id}
                              variant={activeSection === section.id ? "default" : "ghost"}
                              size="default"
                              onClick={() => setActiveSection(section.id)}
                              className="flex-shrink-0 whitespace-nowrap font-medium text-base h-11 sm:flex-1"
                            >
                              <Icon className="w-5 h-5 mr-2" />
                              {section.label}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Content Sections - Enhanced spacing */}
                  <div className="px-6 py-6">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeSection}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {activeSection === 'overview' && (
                          <div className="space-y-8 pb-8">
                          {/* Available Date */}
                          <div className="flex items-center gap-4 p-6 bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl border border-primary/20">
                            <div className="p-3 bg-primary/20 rounded-xl">
                              <Calendar className="w-7 h-7 text-primary" />
                            </div>
                            <div>
                              <p className="text-lg font-semibold text-foreground">Available Date</p>
                              <p className="text-base text-muted-foreground">
                                {formatDate(property.available_date)}
                              </p>
                            </div>
                          </div>

                          {/* Description */}
                          {property.description && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-secondary/20 rounded-xl">
                                  <Home className="w-6 h-6 text-secondary-foreground" />
                                </div>
                                <h3 className="text-xl font-bold">About This Property</h3>
                              </div>
                              <p className="text-muted-foreground leading-relaxed text-base">
                                {property.description}
                              </p>
                            </div>
                          )}

                          {/* Property Details */}
                          <div className="space-y-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2.5 bg-secondary/20 rounded-xl">
                                <Square className="w-6 h-6 text-secondary-foreground" />
                              </div>
                              <h3 className="text-xl font-bold">Property Details</h3>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="flex items-center justify-between p-5 bg-muted/50 rounded-xl border border-border/30">
                                <span className="text-muted-foreground font-medium">Type</span>
                                <span className="capitalize font-bold">{property.property_type}</span>
                              </div>
                              <div className="flex items-center justify-between p-5 bg-muted/50 rounded-xl border border-border/30">
                                <span className="text-muted-foreground font-medium">Square Feet</span>
                                <span className="font-bold">{property.square_feet ? property.square_feet.toLocaleString() : 'N/A'} sqft</span>
                              </div>
                              <div className="flex items-center justify-between p-5 bg-muted/50 rounded-xl border border-border/30">
                                <span className="text-muted-foreground font-medium">Bedrooms</span>
                                <span className="font-bold">{property.bedrooms}</span>
                              </div>
                              <div className="flex items-center justify-between p-5 bg-muted/50 rounded-xl border border-border/30">
                                <span className="text-muted-foreground font-medium">Bathrooms</span>
                                <span className="font-bold">{property.bathrooms}</span>
                              </div>
                              <div className="flex items-center justify-between p-5 bg-muted/50 rounded-xl border border-border/30">
                                <span className="text-muted-foreground font-medium">Lease Term</span>
                                <span className="font-bold">{property.lease_length} months</span>
                              </div>
                              <div className="flex items-center justify-between p-5 bg-muted/50 rounded-xl border border-border/30">
                                <span className="text-muted-foreground font-medium">Deposit</span>
                                <span className="font-bold">{formatPrice(property.deposit || 0)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Pet Policy */}
                          <div className="flex items-center gap-4 p-6 bg-gradient-to-r from-secondary/10 to-secondary/5 rounded-2xl border border-secondary/20">
                            <div className="p-3 bg-secondary/20 rounded-xl">
                              <Home className="w-7 h-7 text-secondary-foreground" />
                            </div>
                            <div>
                              <p className="text-lg font-semibold text-foreground">Pet Policy</p>
                              <p className="text-base text-muted-foreground">
                                {property.pet_policy === 'allowed' && 'Pets Allowed'}
                                {property.pet_policy === 'not_allowed' && 'No Pets'}
                                {property.pet_policy === 'cats_only' && 'Cats Only'}
                                {property.pet_policy === 'dogs_only' && 'Dogs Only'}
                                {!property.pet_policy && 'Not specified'}
                              </p>
                            </div>
                          </div>

                          {/* Amenities Section */}
                          {property.amenities && property.amenities.length > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-secondary/20 rounded-xl">
                                  <Star className="w-6 h-6 text-secondary-foreground" />
                                </div>
                                <h3 className="text-xl font-bold">Amenities</h3>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {property.amenities.map((amenity, index) => (
                                  <div key={index} className="flex items-center gap-3 p-4 bg-muted/40 rounded-xl border border-border/30">
                                    <div className="p-2 bg-primary/20 rounded-lg">
                                      {amenityIcons[amenity] || <Star className="w-5 h-5 text-primary" />}
                                    </div>
                                    <span className="font-medium text-base">{formatAmenityName(amenity)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Utilities Included */}
                          {property.utilities_included && property.utilities_included.length > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-secondary/20 rounded-xl">
                                  <Zap className="w-6 h-6 text-secondary-foreground" />
                                </div>
                                <h3 className="text-xl font-bold">Utilities Included</h3>
                              </div>
                              <div className="flex flex-wrap gap-3">
                                {property.utilities_included.map((utility, index) => (
                                  <Badge key={index} variant="secondary" className="text-base px-4 py-2">
                                    {formatAmenityName(utility)}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {activeSection === 'location' && (
                        <div className="space-y-6 pb-8">
                          <div>
                            <h3 className="text-2xl font-bold mb-3">Location & Map</h3>
                            <p className="text-muted-foreground text-base">
                              Explore the neighborhood and nearby amenities.
                            </p>
                          </div>
                          <PropertyMapWidget property={property} />
                        </div>
                      )}

                        {activeSection === 'fiona' && (
                          <FionaPropertyAnalysis property={property} matchScore={matchScore} />
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </ScrollArea>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PropertyInfoPanel;